import React, { useCallback } from 'react'
import classNames from 'classnames'
import { type CustomIntegration, type CustomIntegrationTool } from '../../../models/CustomIntegration'
import { useRefreshCustomIntegrationToolsMutation } from '../../../store/customIntegration/customIntegration'
import { selectAccountId } from '../../../store/auth/authSlice'
import { useAppDispatch, useAppSelector } from '../../../store/hooks'
import { addToast } from '../../../store/toast/toastSlice'
import { type ErrorResponse } from '../../../common/errorMessages'
import { formatRelativeDateTime } from '../../../common/dateUtils'
import { Button } from '../../../components/Button/Button'
import { WizardStepHeader } from '../components/WizardStepCard'
import styles from './CustomIntegrationToolsTab.module.scss'

interface CustomIntegrationToolsTabProps {
  customIntegration: CustomIntegration
  canRefresh: boolean
  isConnected: boolean
}

const TOOL_NAME_SEPARATOR = '__'

const errorMessageOf = (error: unknown): string | null => {
  const data = (error as { data?: Partial<ErrorResponse> } | null)?.data
  const message = data?.errors?.[0]?.message
  return typeof message === 'string' && message !== '' ? message : null
}

const toolCountLabel = (count: number): string => (count === 1 ? '1 tool' : `${count} tools`)

const refreshBlockerOf = (active: boolean, needsConnection: boolean): string | null => {
  if (!active) {
    return 'Enable the MCP server to refresh its tools.'
  }
  if (needsConnection) {
    return 'Connect to the MCP server to refresh its tools.'
  }
  return null
}

interface ToolListItemProps {
  tool: CustomIntegrationTool
  toolPrefix: string
  withDivider: boolean
}

const ToolListItem: React.FC<ToolListItemProps> = ({ tool, toolPrefix, withDivider }) => (
  <li className={classNames('d-flex align-items-start zocks-gap-0.75 p-3', { 'border-top zocks-border-color-gray-200': withDivider })}>
    <div className={classNames('d-flex align-items-center justify-content-center zocks-bg-color-gray-100 zocks-border-0.5 flex-shrink-0', styles.toolIcon)}>
      <i className='bi bi-lightning-charge zocks-text-gray-700' aria-hidden='true' />
    </div>
    <div className='d-flex flex-column zocks-gap-0.25 min-w-0'>
      <span className='paragraph-medium fw-600 zocks-text-gray-900 text-break'>{tool.name}</span>
      <code className={classNames('paragraph-extra-small zocks-text-gray-600 text-break', styles.toolId)}>
        {toolPrefix}
        {TOOL_NAME_SEPARATOR}
        {tool.name}
      </code>
      {tool.description ? <span className='paragraph-small zocks-text-gray-700 white-space-pre-line'>{tool.description}</span> : null}
    </div>
  </li>
)

interface ToolsEmptyStateProps {
  neverDiscovered: boolean
  canRefresh: boolean
  refreshBlocker: string | null
}

const emptyStateHintOf = ({ neverDiscovered, canRefresh, refreshBlocker }: ToolsEmptyStateProps): string => {
  if (!neverDiscovered) {
    return canRefresh ? 'The last refresh found no tools. Refresh again once the server offers some.' : 'The last discovery found no tools on this MCP server.'
  }
  if (!canRefresh) {
    return 'The tools of this MCP server have not been discovered yet.'
  }
  return refreshBlocker ?? 'Refresh to discover the tools this MCP server offers.'
}

const ToolsEmptyState: React.FC<ToolsEmptyStateProps> = (props) => (
  <div className='d-flex flex-column align-items-center text-center zocks-gap-0.75 py-5 px-4 bg-white border zocks-border-dashed zocks-border-color-gray-300 zocks-border-0.75'>
    <div className={classNames('d-flex align-items-center justify-content-center rounded-circle zocks-bg-color-gray-100', styles.emptyIcon)}>
      <i className='bi bi-tools fs-5 zocks-text-gray-600' aria-hidden='true' />
    </div>
    <span className='paragraph-medium fw-600 zocks-text-gray-900'>{props.neverDiscovered ? 'No tools discovered yet' : 'This MCP server offers no tools'}</span>
    <span className='paragraph-small zocks-text-gray-600'>{emptyStateHintOf(props)}</span>
  </div>
)

export const CustomIntegrationToolsTab: React.FC<CustomIntegrationToolsTabProps> = ({ customIntegration, canRefresh, isConnected }) => {
  const dispatch = useAppDispatch()
  const accountId = useAppSelector(selectAccountId)
  const [refreshTools, { isLoading: isRefreshing }] = useRefreshCustomIntegrationToolsMutation()
  const { id, name, active, auth, toolPrefix, tools, toolsRefreshedAt } = customIntegration

  const discovered = tools ?? []
  const hasTools = discovered.length > 0
  const neverDiscovered = tools === null
  const needsConnection = auth.type !== 'none' && !isConnected
  const refreshBlocker = refreshBlockerOf(active, needsConnection)
  const refreshedLabel = toolsRefreshedAt ? `Refreshed ${formatRelativeDateTime(new Date(toolsRefreshedAt))}` : 'Not discovered yet'

  const handleRefresh = useCallback(async () => {
    try {
      const refreshed = await refreshTools({ accountId, customIntegrationId: id }).unwrap()
      dispatch(addToast({ variant: 'success', title: 'Tools refreshed.', messages: [`${toolCountLabel(refreshed.tools?.length ?? 0)} discovered.`] }))
    } catch (error) {
      dispatch(addToast({ variant: 'error', title: 'Error', messages: [errorMessageOf(error) ?? 'Failed to refresh the tools. Please try again.'] }))
    }
  }, [refreshTools, accountId, id, dispatch])

  return (
    <section className='d-flex flex-column gap-4' aria-label='Tools'>
      <div className='d-flex align-items-start justify-content-between gap-4'>
        <WizardStepHeader icon='bi-tools' title='Tools' subtitle={`What AI agents can do through ${name}.`} />
        {canRefresh ? (
          <Button
            variant='primaryGhost'
            className='d-flex align-items-center zocks-gap-0.5 flex-shrink-0'
            onClick={handleRefresh}
            loading={isRefreshing}
            disabled={refreshBlocker !== null}
          >
            {isRefreshing ? null : <i className='bi bi-arrow-clockwise fs-6' aria-hidden='true' />}
            Refresh tools
          </Button>
        ) : null}
      </div>

      <div className='px-5 d-flex flex-column gap-3'>
        <div className='d-flex align-items-center justify-content-between gap-3 flex-wrap'>
          <div className='d-flex align-items-center zocks-gap-0.75 paragraph-small zocks-text-gray-600'>
            <span
              className={classNames(
                'badge rounded-pill',
                hasTools ? 'zocks-bg-color-success-background zocks-text-success' : 'zocks-bg-color-gray-200 zocks-text-gray-600',
              )}
            >
              {toolCountLabel(discovered.length)}
            </span>
            {canRefresh ? <span>{refreshedLabel}</span> : null}
          </div>
          {refreshBlocker && canRefresh ? (
            <span className='d-flex align-items-center zocks-gap-0.25 paragraph-small zocks-text-warning-text'>
              <i className='bi bi-info-circle' aria-hidden='true' />
              {refreshBlocker}
            </span>
          ) : null}
        </div>

        {hasTools ? (
          <ul className='list-unstyled mb-0 bg-white border zocks-border-color-gray-200 zocks-border-0.75 overflow-hidden' aria-label='Discovered tools'>
            {discovered.map((tool: CustomIntegrationTool, index: number) => (
              <ToolListItem key={tool.name} tool={tool} toolPrefix={toolPrefix} withDivider={index > 0} />
            ))}
          </ul>
        ) : (
          <ToolsEmptyState neverDiscovered={neverDiscovered} canRefresh={canRefresh} refreshBlocker={refreshBlocker} />
        )}
      </div>
    </section>
  )
}
